import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import { IMAGES, ORG, ENGINE11_GALLERY } from "@/lib/constants";
import {
  ArrowUpRight,
  Phone,
  Mail,
  Compass,
  Building2,
  Users,
  GraduationCap,
  HeartHandshake,
  Sparkles,
  BookOpen,
  Flame,
  Wrench,
  ShieldCheck,
  Accessibility,
  Zap,
  Calendar,
  Award,
  Camera,
} from "lucide-react";

const TIMELINE = [
  { y: "1883", t: "Historic Engine 11 Built", b: "Historic Engine 11 is built at 2733 Gratiot Avenue on Detroit's east side — a working firehouse serving Detroit's growing fire service needs." },
  { y: "1967", t: "Phoenix is Founded", b: "Phoenix is founded in Detroit, following the events of 1967, as a voice for minority firefighters and a liaison between the Detroit Fire Department and the community." },
  { y: "2002", t: "Phoenix Purchases Engine 11", b: "After standing vacant for nearly twelve years, Historic Engine 11 is purchased by Phoenix — establishing a permanent home for advocacy, mentorship, preservation, and service." },
  { y: "2027", t: "60 Years of Phoenix", b: "Engine 11 stands at the center of the Phoenix 60-year milestone — a continued home for legacy, service, and the Phoenix mission." },
];

const INSIDE_FIREHOUSE_DEPRECATED = null; // Section removed Feb 2026 — placeholder tiles will return when real photos + descriptions are available.

const WHATS_HAPPENING = [
  { icon: GraduationCap, t: "Project 100 Sessions", to: "/programs-outreach#project-100" },
  { icon: HeartHandshake, t: "Community Outreach Days", to: "/programs-outreach" },
  { icon: Users, t: "Retiree Fellowship", to: "/membership" },
  { icon: BookOpen, t: "Membership Meetings", to: "/membership" },
  { icon: Sparkles, t: "Mentorship", to: "/programs-outreach#project-100" },
  { icon: Building2, t: "Private Rentals", to: "/rent-engine-11" },
];

const PRESERVATION_AREAS = [
  { icon: Wrench, t: "Ongoing Preservation Work" },
  { icon: Building2, t: "Building Improvements" },
  { icon: Accessibility, t: "Accessibility Improvements" },
  { icon: Zap, t: "Utilities & Security" },
  { icon: Calendar, t: "Community Programming" },
  { icon: Award, t: "Memorial & Legacy Displays" },
  { icon: Sparkles, t: "Phoenix Anniversaries & Milestones" },
];

export default function HistoricEngine11() {
  const mapsHref = `https://www.google.com/maps?q=${encodeURIComponent(ORG.mapsQuery)}`;

  return (
    <div data-testid="engine11-page">
      <PageMeta
        title="Historic Engine 11"
        description="A storied Detroit firehouse and the home of the Phoenix — where preservation, service, mentorship, active and retired firefighter fellowship, and Detroit fire service legacy live under one roof."
        path="/historic-engine-11"
      />
      <PageHero
        overline="Headquarters · Detroit Landmark"
        title="Historic Engine 11."
        subtitle="A storied Detroit firehouse and the home of the Phoenix — a place where preservation, service, and legacy live under one roof."
        image={IMAGES.engine11}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <Link to="/rent-engine-11" className="btn-gold w-full sm:w-auto justify-center" data-testid="hero-rent-btn">
            Rent Engine 11 <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/donate-sponsor" className="btn-primary w-full sm:w-auto justify-center" data-testid="hero-support-btn">
            Support Preservation
          </Link>
          <Link
            to="/contact?type=rent-engine-11&subject=Rental%20Walkthrough%20Request"
            className="btn-ghost w-full sm:w-auto justify-center"
            data-testid="hero-walkthrough-btn"
          >
            Schedule a Rental Walkthrough
          </Link>
        </div>
      </PageHero>

      {/* ----- DIRECT CONTACT / LOCATION STRIP ----- */}
      <section className="px-5 lg:px-8 mt-8 lg:mt-12" data-testid="engine11-direct">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch">
          <a href={`tel:${ORG.phone.replace(/[^0-9+]/g, "")}`} className="card-dark p-5 flex items-start gap-4 hover:border-amber-500/40">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Phone className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <div className="overline">Phone</div>
              <div className="text-white text-sm mt-1">{ORG.phone}</div>
            </span>
          </a>
          <a
            href={ORG.emailConfirmed ? `mailto:${ORG.email}` : undefined}
            className={`card-dark p-5 flex items-start gap-4 ${ORG.emailConfirmed ? "hover:border-amber-500/40" : "opacity-80"}`}
            aria-disabled={!ORG.emailConfirmed}
          >
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Mail className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <div className="overline">Email</div>
              <div className="text-white text-sm mt-1 break-all">{ORG.emailConfirmed ? ORG.email : "Email coming soon"}</div>
            </span>
          </a>
          <a href={mapsHref} target="_blank" rel="noreferrer" className="card-dark p-5 flex items-start gap-4 hover:border-amber-500/40">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Compass className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <div className="overline">Map / Directions</div>
              <div className="text-white text-sm mt-1 leading-relaxed">
                Historic Engine 11<br />
                2733 Gratiot Ave.<br />
                Detroit, MI 48207
              </div>
            </span>
          </a>
        </div>
      </section>

      {/* ----- STEWARDSHIP CLARIFICATION ----- */}
      <section className="px-5 lg:px-8 mt-8" data-testid="engine11-stewardship">
        <div className="max-w-7xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-black">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div>
            <div className="overline">Stewardship</div>
            <p className="mt-2 text-slate-100 text-base lg:text-lg leading-relaxed max-w-4xl">
              Historic Engine 11 serves as the headquarters of the Phoenix of the
              Detroit Fire Department. Phoenix operates from and stewards Engine 11
              as the home of the organization and a living symbol of Detroit fire
              service history.
            </p>
          </div>
        </div>
      </section>

      {/* ----- LIVING LANDMARK (story + at-a-glance) ----- */}
      <section className="py-12 lg:py-28" data-testid="engine11-landmark">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-5 text-slate-300 leading-relaxed text-lg">
            <SectionHeading overline="A Living Landmark" title="More than a firehouse." />
            <p>
              Historic Engine 11 has stood watch over Detroit for generations. Today,
              as the headquarters of the Phoenix, it is both a working symbol of the
              city's firefighting legacy and a community gathering place — preserved,
              respected, and carried forward.
            </p>
            <p>
              The walls hold memory: the calls answered, the lives saved, the
              brotherhood and sisterhood that shaped Detroit's fire service. Engine
              11 is where that story continues to be told.
            </p>
            <p>
              Engine 11 hosts community programs, mentorship sessions, ceremonies,
              and private rentals — every visit underwrites the next chapter of
              preservation and service.
            </p>
          </div>
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-2xl overflow-hidden ring-ember">
              <img src={IMAGES.engine11} alt="Historic Engine 11 firehouse" className="w-full h-full object-cover" />
            </div>
            <div className="card-dark p-6">
              <div className="overline">At a Glance</div>
              <ul className="mt-4 text-slate-300 space-y-2 text-sm">
                <li>• Historic Detroit firehouse</li>
                <li>• Phoenix nonprofit headquarters</li>
                <li>• Available for private rentals & events</li>
                <li>• Community programming hub</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ----- PRESERVATION TIMELINE ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="engine11-timeline">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Preservation"
            title="Every brick has a story."
            subtitle="Engine 11 is preserved through the generosity of donors, sponsors, and renters. Help us keep the legacy standing."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIMELINE.map((c) => (
              <div key={c.y + c.t} className="card-dark p-7">
                <div className="font-accent text-amber-500 text-3xl lg:text-4xl tracking-wider">{c.y}</div>
                <div className="font-display text-xl text-white mt-3">{c.t}</div>
                <div className="text-slate-400 mt-2 text-sm leading-relaxed">{c.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- WHAT HAPPENS AT ENGINE 11 ----- */}
      <section className="py-12 lg:py-24" data-testid="engine11-happens">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="What Happens at Engine 11"
            title="A working home for the Phoenix mission."
            subtitle="The walls don't just hold history — they hold the ongoing work."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {WHATS_HAPPENING.map((w) => {
              const Inner = (
                <>
                  <w.icon className="h-6 w-6 text-amber-500 mx-auto" />
                  <div className="mt-3 font-display text-sm lg:text-base text-white leading-tight">{w.t}</div>
                  {w.to && (
                    <div className="mt-2 text-[10px] tracking-[0.22em] uppercase font-accent text-amber-500">Learn More →</div>
                  )}
                </>
              );
              return w.to ? (
                <Link
                  key={w.t}
                  to={w.to}
                  className="card-dark p-6 text-center hover:border-amber-500/40 group"
                  data-testid={`happens-${w.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {Inner}
                </Link>
              ) : (
                <div key={w.t} className="card-dark p-6 text-center" data-testid={`happens-${w.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {Inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ----- INSIDE HISTORIC ENGINE 11 — restrained 6-photo gallery (added May 23, 2026) ----- */}
      <section className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="engine11-gallery">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-amber-400 text-xs tracking-[0.32em] uppercase font-accent">
              <Camera className="h-3.5 w-3.5" /> Inside Engine 11
            </div>
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white mt-4 leading-tight">
              Inside Historic Engine 11.
            </h3>
            <div className="divider-gold" />
            <p className="text-slate-400 mt-6 text-sm sm:text-base leading-relaxed">
              The banquet hall, the bar, the foyer of honored portraits, and the
              upstairs meeting room — a small look inside the spaces where the
              Phoenix mission lives and where private rentals are hosted.
            </p>
          </div>

          <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {ENGINE11_GALLERY.map((g, i) => (
              <figure
                key={g.src}
                className="group relative overflow-hidden rounded-2xl border border-slate-800 ring-1 ring-amber-500/10 bg-black/40"
                data-testid={`engine11-gallery-item-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading={i < 3 ? "eager" : "lazy"}
                    className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-4 py-3">
                  <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-amber-400">{g.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ----- PHOENIX LEGACY PROJECT · FACES NEVER FORGOTTEN ----- */}
      <section className="py-12 lg:py-24" data-testid="engine11-legacy-project">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="The Phoenix Legacy Project"
            title="Faces never forgotten."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-2xl border border-slate-800 ring-1 ring-amber-500/10">
                <img
                  src="/photos/legacy-project-wall.jpg"
                  alt="Wall of framed historic Detroit firefighter portraits inside Historic Engine 11 — the physical heart of the Phoenix Legacy Project."
                  loading="lazy"
                  className="w-full h-auto block"
                />
              </div>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-300 leading-relaxed">
                At Historic Engine 11, the walls remember. The Phoenix Legacy
                Project is our ongoing work to honor the full Phoenix family —
                active Detroit firefighters serving today, retired Phoenix
                legacy leaders, and the trailblazers who came before — by
                preserving their photographs, their stories, and their place
                in Detroit's fire service history.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mt-5">
                This is the quiet work of memory and the active work of
                continuity: photographs framed, names spoken, careers traced
                back through nearly six decades of Phoenix and beyond — and
                forward into the next generation of Detroit firefighters.
              </p>
              <blockquote
                className="mt-8 border-l-2 border-amber-500/60 pl-5 italic text-amber-100/90 leading-relaxed"
                data-testid="legacy-shoulders-quote"
              >
                "We stand on the shoulders of those who came before us."
              </blockquote>
            </div>
          </div>

          {/* ----- Phoenix Legacy Project FLYER (restrained pre-launch surfacing) ----- */}
          <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid="engine11-legacy-flyer">
            <div className="lg:col-span-5 order-1">
              <div className="overline text-amber-500 mb-3">Official Project Flyer</div>
              <h3 className="font-display text-2xl text-white leading-tight">
                Faces Never Forgotten.
              </h3>
              <p className="text-slate-300 leading-relaxed mt-4">
                The Phoenix Legacy Project preserves the faces, names, and
                stories of active and retired Detroit firefighters for future
                generations.
              </p>
              <p className="text-slate-500 text-xs leading-relaxed mt-4">
                Submit fire-department photos (action shots in turnout gear,
                dress uniform, or station wear) sized 8×10, 4×6, or 3×5 to
                <a href="mailto:8phoenixrising@gmail.com" className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline ml-1">8phoenixrising@gmail.com</a>.
              </p>
            </div>
            <div className="lg:col-span-7 order-2">
              <a
                href="/events/phoenix-legacy-project-flyer.jpg?v=2"
                target="_blank"
                rel="noreferrer"
                className="block overflow-hidden rounded-2xl border border-amber-500/30 hover:border-amber-500/60 transition-colors bg-black/40"
                data-testid="legacy-flyer-link"
                aria-label="Open the Phoenix Legacy Project flyer in a new tab"
              >
                <img
                  src="/events/phoenix-legacy-project-flyer.jpg?v=2"
                  alt="Phoenix Legacy Project flyer — Faces Never Forgotten. Submit fire-department photos to be featured on the legacy wall at Historic Engine 11."
                  loading="eager"
                  className="w-full h-auto block"
                />
              </a>
              <p className="text-slate-500 text-xs leading-relaxed mt-3">
                Tap or click the flyer to open the full-size version.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- PRESERVATION SUPPORT ----- */}
      <section className="py-12 lg:py-24" data-testid="engine11-preservation">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Preservation Support"
            title="What your support helps sustain."
            subtitle="Donor, sponsor, and rental support directly helps maintain the working life of Historic Engine 11."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {PRESERVATION_AREAS.map((p) => (
              <div key={p.t} className="card-dark p-5 text-center" data-testid={`preserve-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p.icon className="h-6 w-6 text-amber-500 mx-auto" />
                <div className="mt-3 font-display text-sm lg:text-base text-white leading-tight">{p.t}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/donate-sponsor" className="btn-gold" data-testid="preserve-cta">
              Support Preservation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ----- HOW TO VISIT removed — merged into Closing CTA below ----- */}

      {/* (2027 bridge removed — evergreen Historic Engine 11 page) */}

      {/* ----- CLOSING CTA ----- */}
      <section className="py-16 lg:py-32" data-testid="engine11-closing">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="overline">Help Carry the Legacy</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-[1.05]">
            Help us keep the
            <span className="block text-amber-500">legacy standing.</span>
          </h2>
          <div className="divider-gold mx-auto" />
          <p className="text-slate-400 mt-6 leading-relaxed max-w-2xl mx-auto">
            Historic Engine 11 visits, walkthroughs, and tours are scheduled by
            appointment. Private rental inquiries route through the rental page.
            Public events are announced on Events & News.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/rent-engine-11" className="btn-gold w-full justify-center" data-testid="closing-rent">Rent Engine 11</Link>
            <Link to="/donate-sponsor" className="btn-primary w-full justify-center" data-testid="closing-support">Support Preservation</Link>
            <Link to="/contact?type=rent-engine-11&subject=Rental%20Walkthrough%20Request" className="btn-ghost w-full justify-center" data-testid="closing-visit">Schedule a Visit</Link>
            <Link to="/contact" className="btn-ghost w-full justify-center" data-testid="closing-contact">Contact Phoenix</Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3 text-xs text-slate-500">
            <Flame className="h-3.5 w-3.5 text-amber-500" />
            <span className="tracking-[0.28em] uppercase font-accent">Preserving Our History · Creating the Future</span>
          </div>
        </div>
      </section>
    </div>
  );
}
